import React, { BaseSyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { enqueueSnackbar } from 'notistack';

import { Close as CloseIcon, DialogSection, IOption, Notice, NoticeStack, Nullable } from '@linetweet/linetweet-ui';
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import { getLocaleByStore, isArrayEqual, TimeHelper } from 'utils';
import { AsyncButton, EmployeeSelectField, GetAppointmentCustomer, GetAppointmentServices } from 'features/commons';
import { Appointment, AppointmentEvent, CalendarEventTypeEnum, IUpdateCalendarEventQueryParams, Service } from 'types';
import { deleteCalendarEvents, getCalendarEvent, updateCalendarEvents } from 'store/appointments/thunks';
import { getSeriesById, recordEventOutcome, updateSeries } from 'store/series/thunks';
import { updateCustomer } from 'store/customers/thunks';
import { resetGetSeriesById } from 'store/series/slice';
import { getSlots, getSlotsByDate, getSwapSlots } from 'store/slots/thunks';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { yupResolver } from '@hookform/resolvers/yup';

import { shallowEqual } from 'react-redux';
import { selectAvailableDates } from 'store/slots/selectors';
import { resetDaySlots, resetSlots, resetSwapSlots } from 'store/slots/slice';
import { IGetSlotsByDatesPayload } from 'store/slots/types';
import {
  AppointmentFormCustomerSection,
  AppointmentFormInformationSection,
  AppointmentFormServiceSection,
  AppointmentFormTimeSection,
  SeriesMemberEditBody,
  SlotSelectSection,
} from './components';
import { RecordOutcomeModal } from './components/RecordOutcomeModal';
import {
  AppointmentFormSlotItem,
  CalendarEventCreateParams,
  EditCalendarEventFormProps,
  IAppointmentFormValues,
  IBlockerFormValues,
  IBreakFormValues,
  ServiceTabsSelectOption,
} from './types';
import { calendarEventToCalendarCreateValues, createSlotOptions, eventToFormSliceValues, getDateRange } from './helpers';
import { BookingChannelBadge, FormDatePicker } from './subcomponents';
import { selectAvailableEmployees, selectSlotEmployeesByServiceIds } from '../../store/employees/selectors';
import { selectCurrentStore, selectStoreServices } from '../../store/store/selectors';
import { isRequired } from './util/isRequired';
import { fetchEmployees } from '../../store/employees/thunks';
import { resetUpdateCalendarEvent } from '../../store/appointments/slice';
import { appointmentFormSchema } from './util/appointmentFormSchema';
import { breakFormSchema } from './util/breakFormSchema';
import { blockerFormSchema } from './util/blockerFormSchema';
import { createEmployeeOptions } from './util/createEmployeeOptions';
import { createSwapSlotOptions } from './util/createSwapSlotOptions';

import styles from './EditCalendarEvent.module.scss';

type Props = {
  appointmentId: string;
  type: CalendarEventTypeEnum;
  employeeId?: string;
  onClose: () => void;
  onCopyToNew: (params: CalendarEventCreateParams) => void;
  shouldReload?: boolean;
  isPastEvent?: boolean;
};

const calendarEventToAppointmentFormValues = (
  calendarEvent: Appointment,
  allServices: Service[],
  intl: IntlShape,
): IAppointmentFormValues => {
  const customer = GetAppointmentCustomer(calendarEvent);
  const services: Service[] = GetAppointmentServices(calendarEvent, allServices);

  return {
    date: calendarEvent.date,
    note: calendarEvent.note || null,
    slot: {
      label: TimeHelper.getTimeByMinutes(calendarEvent.time),
      value: calendarEvent.time,
    },
    gender: customer?.gender || null,
    services: services.map(
      (service): ServiceTabsSelectOption => ({
        label: intl.formatMessage({ id: service.name }),
        value: service.id,
        name: service.name,
        tabId: service.category,
        extra: { ...service },
      }),
    ),
    employee: (calendarEvent.eventTypeData as AppointmentEvent).suggestedEmployee || null,
    title: calendarEvent.title || null,
    lastName: customer?.lastName || '',
    firstName: customer?.name || '',
    email: customer?.email || '',
    phoneNumber: customer?.phoneNumber || '',
    pinnedEmployee: calendarEvent?.pinnedEmployee || '',
  };
};

const calendarEventToBreakFormValues = (calendarEvent: Appointment): IBreakFormValues => ({
  date: calendarEvent.date,
  note: calendarEvent.note || null,
  employee: (calendarEvent.eventTypeData as AppointmentEvent).suggestedEmployee || null,
  title: calendarEvent.title || null,
  recurring: null,
  from: calendarEvent.time,
  to: calendarEvent.time + (calendarEvent.duration || 0),
});

const calendarEventToBlockerFormValues = (calendarEvent: Appointment): IBlockerFormValues => ({
  date: calendarEvent.date,
  note: calendarEvent.note || null,
  employee: (calendarEvent.eventTypeData as AppointmentEvent).suggestedEmployee || null,
  title: calendarEvent.title || null,
  from: calendarEvent.time,
  to: calendarEvent.time + (calendarEvent.duration || 0),
});

export function EditAppointmentForm({
  appointment, onCopy, onBookFollowUp, onCopyFromSeries, onClose, onDelete, isPastEvent, onSubmit: propsOnSubmit,
}: EditCalendarEventFormProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const allServices = useAppSelector(selectStoreServices, shallowEqual);

  const form = useForm<IAppointmentFormValues>({
    defaultValues: calendarEventToAppointmentFormValues(appointment, allServices, intl),
    resolver: yupResolver(appointmentFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const servicesValue = useWatch({ control: form.control, name: 'services' });
  const dateValue = useWatch({ control: form.control, name: 'date' });
  const employeeValue = useWatch({ control: form.control, name: 'employee' });
  const slotValue = useWatch({ control: form.control, name: 'slot' });

  const serviceIds = useMemo(() => servicesValue.map(({ value }) => value), [servicesValue]);

  const store = useAppSelector(selectCurrentStore);
  const storeEmployees = useAppSelector((state) => state.employees.storeEmployees.data.employees, shallowEqual);
  const employeesLoading = useAppSelector((state) => state.employees.storeEmployees.loading);
  const daySlotsState = useAppSelector((state) => state.slots.dateSlots, shallowEqual);
  const updateCalendarEventState = useAppSelector((state) => state.appointments.updateCalendarEvent);
  const availableDays = useAppSelector((state) => selectAvailableDates(state, serviceIds, employeeValue), shallowEqual);
  const slotEmployees = useAppSelector((state) => selectSlotEmployeesByServiceIds(state, serviceIds, dateValue), shallowEqual);
  const availableEmployees = useAppSelector((state) => selectAvailableEmployees(state, serviceIds, dateValue), shallowEqual);
  const swapSlotsState = useAppSelector((state) => state.slots.swapSlots, shallowEqual);

  const isSeriesMember = Boolean(appointment.seriesId);
  const seriesState = useAppSelector((state) => state.series.getSeriesById, shallowEqual);

  // editingEventId is the id of the series event currently in inline-edit
  // mode. Only one card can be in edit mode at a time.
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  // outcomeEventId scopes the OutcomeModal to a specific event in the series.
  const [outcomeEventId, setOutcomeEventId] = useState<string | null>(null);
  // undoingEventId marks the event whose undo request is in flight, so its
  // row can show a spinner and disable the control.
  const [undoingEventId, setUndoingEventId] = useState<string | null>(null);

  // Series cards are read-only until the user clicks "edit" on one. Until
  // then, slots/employees/swap fetches are wasted work — gate them on the
  // inline-edit panel actually being open. Non-series edits always load.
  const shouldLoadSchedulingData = !isSeriesMember || editingEventId !== null;

  // For series members, customer data + series.note are always editable —
  // closed series still allow staff to fix typos / update contact info.
  // Non-series members keep the existing past-event read-only rule.
  const customerSectionDisabled = isSeriesMember ? false : Boolean(isPastEvent);

  // Resolve the full Appointment for editingEventId. If the id matches the
  // originally-opened appointment we use that; otherwise we look it up in the
  // series events. Used by handleSubmit to PATCH the right event.
  const editingEvent: Appointment | null = useMemo(() => {
    if (editingEventId === null) return null;
    if (editingEventId === appointment.id) return appointment;
    if (seriesState.data === null) return null;
    const found = seriesState.data.events.find((e) => e.id === editingEventId);
    return found || null;
  }, [editingEventId, appointment, seriesState.data]);

  const fetchSeries = useCallback(() => {
    if (appointment.seriesId) {
      dispatch(getSeriesById({ id: appointment.seriesId }));
    }
  }, [dispatch, appointment.seriesId]);

  useEffect(() => {
    if (isSeriesMember) {
      fetchSeries();
    }
    return () => {
      if (isSeriesMember) {
        dispatch(resetGetSeriesById());
      }
    };
  }, [isSeriesMember, fetchSeries, dispatch]);

  // Toggle inline edit for a specific event in the series. Resets the form's
  // event-level fields (date/slot/employee/services) to the event's original
  // values via resetField — this also clears any dirty flags from a prior
  // event's open edit, so switching A→B reverts A to its pristine state.
  // Customer/note fields are series-level and intentionally untouched here;
  // they're saved via the top-level customer Save button independently.
  const handleToggleEditEvent = useCallback((eventId: string) => {
    let target: Appointment | null = null;
    if (eventId === appointment.id) {
      target = appointment;
    } else if (seriesState.data) {
      target = seriesState.data.events.find((e) => e.id === eventId) || null;
    }
    if (!target) {
      return;
    }
    const slice = eventToFormSliceValues(target, allServices, intl);
    form.resetField('date', { defaultValue: slice.date });
    form.resetField('slot', { defaultValue: slice.slot });
    form.resetField('employee', { defaultValue: slice.employee });
    form.resetField('services', { defaultValue: slice.services });
    setEditingEventId(editingEventId === eventId ? null : eventId);
  }, [editingEventId, appointment, seriesState.data, allServices, form, intl]);

  // Undo the outcome on any outcome-bearing event of the series (LT-4074). Reuses the
  // record-outcome route with `outcome: null`; the backend clears the outcome,
  // resurrects the event if it was cancelled, and reactivates a closed series.
  // On success we refetch the series so the reopened status and restored
  // appointment reflect, and surface a confirmation toast.
  const handleUndoEvent = useCallback(async (eventId: string) => {
    setUndoingEventId(eventId);

    try {
      await dispatch(recordEventOutcome({ eventId, outcome: null })).unwrap();

      setTimeout(() => {
        fetchSeries();
        setUndoingEventId(null);
        enqueueSnackbar(intl.formatMessage({ id: 'seriesbooking.series.undo.reverted' }), {
          variant: 'success',
        });
      }, 2000);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage({ id: 'common.defaultError' }), {
        variant: 'error',
      });
      setUndoingEventId(null);
    }
  }, [dispatch, intl, fetchSeries]);

  // Seed `note` from series.note once the series payload arrives. Series-
  // member appointments display/edit the *series* note, not the event note,
  // but the series fetch is async so the form mounts with whatever
  // calendarEventToAppointmentFormValues seeded (event.note). When the data
  // lands, swap it in — but only if the user hasn't already typed something
  // (don't clobber dirty input). Non-series and dirty cases are no-ops.
  useEffect(() => {
    if (!isSeriesMember || seriesState.data === null) return;
    if (form.formState.dirtyFields.note) return;
    form.resetField('note', { defaultValue: seriesState.data.series.note ?? '' });
  }, [isSeriesMember, seriesState.data]);

  // Build (targetId, params) from form values. Shared by the regular submit
  // path (propsOnSubmit → optimistic close) and the series inline-edit
  // submit path (await result → snackbar on failure).
  const buildSubmitArgs = useCallback((values: IAppointmentFormValues): {
    targetId: string;
    params: IUpdateCalendarEventQueryParams;
  } => {
    const serviceValues = values.services.map((serviceOption) => serviceOption.value);

    let time = 0;
    let swap;
    if (values.slot && values.slot.value) {
      time = values.slot.value;
      if (values.slot.extra && values.slot.extra.isSwap) {
        swap = true;
      }
    }

    let duration: number = values.services.reduce((acc, service) => acc + service.extra.duration, 0);
    if (duration !== appointment.duration) {
      const services = values.services.map((service) => service.value);
      if (
        isArrayEqual(services, (appointment.eventTypeData as AppointmentEvent).services)
        && appointment.time === time
        && appointment.date === values.date
      ) {
        // if services are equal and start time is equal as well, we keep appointment initial duration
        duration = appointment.duration;
      }
    }

    // Per-event series save: omit customer-related fields (and the
    // customer-derived `title`). Backend's spread-merge preserves the event's
    // existing eventTypeData.customer/customerId and event.title, and
    // resolveCalendarEventCustomerChanges short-circuits to no-op when
    // customer/customerId are absent. Non-series edits never set
    // editingEventId, so they take the full-payload branch unchanged.
    let params: IUpdateCalendarEventQueryParams;
    if (editingEvent !== null) {
      params = {
        duration,
        date: values.date,
        time,
        swap,
        type: CalendarEventTypeEnum.APPOINTMENT,
        eventTypeData: {
          services: serviceValues,
          suggestedEmployee: typeof values.employee !== 'undefined' ? values.employee : undefined,
        },
      };
    } else {
      const title: Nullable<string> = [values.firstName, values.lastName].join(' ').trim();
      const eventTypeData = appointment.eventTypeData as AppointmentEvent;
      let locale: string | null = null;
      if (eventTypeData.customer && eventTypeData.customer.locale) {
        locale = eventTypeData.customer.locale;
      }

      params = {
        duration,
        date: values.date,
        time,
        swap,
        type: CalendarEventTypeEnum.APPOINTMENT,
        title,
        note: values.note || '',
        eventTypeData: {
          services: serviceValues,
          suggestedEmployee: typeof values.employee !== 'undefined' ? values.employee : undefined,
          customerId: eventTypeData.customerId,
          customer: {
            name: values.firstName,
            lastName: values.lastName,
            phoneNumber: values.phoneNumber,
            email: values.email,
            gender: typeof values.gender !== 'undefined' ? values.gender : undefined,
            ...(locale && { locale }),
          },
        },
      };
    }

    const targetId = editingEvent ? editingEvent.id : appointment.id;
    return { targetId, params };
  }, [appointment, editingEvent]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const { targetId, params } = buildSubmitArgs(values);
    await propsOnSubmit(targetId, params);
  });

  // Series customer-section save: split into two independent calls — customer
  // fields go to PATCH /customers/:id, the series note goes to PATCH /series/:id.
  // Each side is skipped when nothing in its scope is dirty; if neither is
  // dirty we just close. Fire-and-close (no await) — matches the prior
  // optimistic-close behavior of the combined calendar-events save.
  // Series customer-section save: dispatches updateCustomer and updateSeries
  // in parallel (only the dirty side actually fires) and awaits both before
  // closing. If either rejects we surface a single error toast and stay open
  // so the user can retry; if both succeed (or nothing was dirty) we close.
  const handleSubmitSeriesCustomer = form.handleSubmit(async (values) => {
    const dirty = form.formState.dirtyFields;
    const customerDirty = Boolean(
      dirty.firstName || dirty.lastName || dirty.email || dirty.phoneNumber || dirty.gender,
    );
    const noteDirty = Boolean(dirty.note) && seriesState.data !== null;

    const showError = () => {
      enqueueSnackbar(intl.formatMessage({ id: 'common.defaultError' }), {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        preventDuplicate: true,
      });
    };

    const eventTypeData = appointment.eventTypeData as AppointmentEvent;
    const customerPromise = customerDirty && eventTypeData.customerId
      ? dispatch(updateCustomer({
        id: eventTypeData.customerId,
        name: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        gender: values.gender ?? undefined,
      }))
      : null;

    const seriesPromise = noteDirty && appointment.seriesId
      ? dispatch(updateSeries({ id: appointment.seriesId, note: values.note || '' }))
      : null;

    try {
      const [customerResult, seriesResult] = await Promise.all([customerPromise, seriesPromise]);

      const customerFailed = customerResult !== null && updateCustomer.rejected.match(customerResult);
      const seriesFailed = seriesResult !== null && updateSeries.rejected.match(seriesResult);

      if (customerFailed || seriesFailed) {
        showError();
        return;
      }
    } catch {
      showError();
      return;
    }

    onClose();
  });

  // Series inline-edit save: awaits the dispatch and surfaces failures via a
  // toast instead of optimistically closing. Scoped to the inline-edit panel
  // only — every other Save still flows through `handleSubmit` /
  // `propsOnSubmit` and keeps the original close-on-dispatch behavior.
  const handleSubmitInlineEdit = form.handleSubmit(async (values) => {
    const { targetId, params } = buildSubmitArgs(values);
    const result = await dispatch(updateCalendarEvents({ appointmentId: targetId, params }));
    if (updateCalendarEvents.rejected.match(result)) {
      enqueueSnackbar(intl.formatMessage({ id: 'common.defaultError' }), {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
        preventDuplicate: true,
      });
      return;
    }
    onClose();
  });

  // Apply the slot-overflow workaround before invoking a form submit handler.
  // Extracted so both `onSubmit` and `onSubmitInlineEdit` share it.
  const applySlotOverflowFallback = useCallback(() => {
    const formValues = form.watch();
    const dirtyFields = Object.keys(form.formState.dirtyFields);
    const allowedFieldsForOverflowOverride = ['email', 'gender', 'firstName', 'lastName', 'note', 'phoneNumber', 'title'];
    const addSlotForOverflow =
      dirtyFields.length === dirtyFields.filter((field) => allowedFieldsForOverflowOverride.includes(field)).length;

    // eslint-disable-next-line no-underscore-dangle
    if (!formValues.slot && addSlotForOverflow && form.control._defaultValues.slot) {
      // eslint-disable-next-line no-underscore-dangle
      const defaultSlot = form.control._defaultValues.slot;

      if (defaultSlot) {
        const slot = {
          label: defaultSlot?.label ?? '',
          value: defaultSlot?.value ?? 0,
        };
        form.setValue('slot', slot, { shouldValidate: true });
      }
    }
  }, [form]);

  const onSubmit = useCallback(
    (e: BaseSyntheticEvent<object, any, any> | undefined) => {
      applySlotOverflowFallback();
      return handleSubmit(e);
    },
    [applySlotOverflowFallback, handleSubmit],
  );

  const onSubmitInlineEdit = useCallback(
    (e: BaseSyntheticEvent<object, any, any> | undefined) => {
      applySlotOverflowFallback();
      return handleSubmitInlineEdit(e);
    },
    [applySlotOverflowFallback, handleSubmitInlineEdit],
  );

  const slotOptions = useMemo(() => {
    const isRelatedFieldsDirty = form.formState.dirtyFields.date || form.formState.dirtyFields.services;

    let defaultSlot;
    // eslint-disable-next-line no-underscore-dangle
    if (!isRelatedFieldsDirty && appointment && form.control._defaultValues.slot) {
      const defaultEmployeeId = appointment.pinnedEmployee;
      const isSplit = (appointment.eventTypeData as AppointmentEvent).split;

      if (defaultEmployeeId && (!employeeValue || (employeeValue === defaultEmployeeId && !isSplit))) {
        defaultSlot = {
          // eslint-disable-next-line no-underscore-dangle
          ...form.control._defaultValues.slot,
          pinnedEmployee: appointment.pinnedEmployee,
        };
      }
    }
    return createSlotOptions({
      timezone: store.timezone,
      employeeId: employeeValue,
      slots: daySlotsState.data || [],
      defaultSlot,
      previousEmployeeId: form.formState.defaultValues?.employee,
      pinnedEmployee: defaultSlot?.pinnedEmployee,
    });
  }, [daySlotsState, store, employeeValue, appointment]);

  const swapSlotOptions = useMemo<AppointmentFormSlotItem[] | undefined>(
    () => (swapSlotsState.data ? createSwapSlotOptions(swapSlotsState.data, dateValue, store.timezone) : undefined),
    [swapSlotsState.data, dateValue, store],
  );

  const employeeOptions = useMemo(
    () => (
      createEmployeeOptions({
        employeeId: employeeValue,
        storeEmployees,
        availableEmployees,
      })
    ),
    [availableEmployees, employeeValue, storeEmployees],
  );

  const fetchSwapOptions = useCallback(() => {
    if (store.settings.swap && employeeValue) {
      dispatch(
        getSwapSlots({
          date: dateValue,
          employeeId: employeeValue,
          serviceIds,
          timestamp: Date.now(),
          timezone: store.timezone,
        }),
      );
    }
  }, [employeeValue, dateValue, employeeValue, serviceIds, store]);

  const isCopyEnabled = useMemo(() => {
    if (!appointment) {
      return false;
    }
    const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
    return isPastEvent || !isDirty;
  }, [appointment, form.formState, isPastEvent]);

  // Series "copy to new" eligibility. Disabled when:
  // - any customer-section field is dirty (only customer fields render at the
  //   top of the series modal, so the generic dirty check matches the spec),
  // - the parent didn't pass `onCopyFromSeries` (settings.series.firstServiceId
  //   missing — the parent gates rendering of the action by returning undefined).
  const isSeriesCopyEnabled = useMemo(() => {
    if (!isSeriesMember || !onCopyFromSeries) return false;
    const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
    return !isDirty;
  }, [isSeriesMember, onCopyFromSeries, form.formState]);

  useEffect(() => {
    if (updateCalendarEventState.error) {
      dispatch(
        getSlotsByDate({
          serviceIds,
          date: dateValue,
          timestamp: Date.now(),
          timezone: store.timezone,
        }),
      );

      if (swapSlotsState.data) {
        fetchSwapOptions();
      }
    }
  }, [updateCalendarEventState.error]);

  useEffect(() => {
    if (daySlotsState.loading) {
      return;
    }

    let slotToSet: AppointmentFormSlotItem | null = null;

    const optionByValueMap: Record<number, IOption> = {};
    for (let optionIndex = 0; optionIndex < slotOptions.length; optionIndex += 1) {
      const option = slotOptions[optionIndex];
      optionByValueMap[option.value] = option;
    }

    const possibleValues: AppointmentFormSlotItem[] = [];
    if (slotValue) {
      possibleValues.push(slotValue);
    }

    // eslint-disable-next-line no-underscore-dangle
    if (form.control._defaultValues.slot) {
      // eslint-disable-next-line no-underscore-dangle
      possibleValues.push(form.control._defaultValues.slot as AppointmentFormSlotItem);
    }

    const possibleValue = possibleValues.find((slotOption) => optionByValueMap[slotOption.value]);

    if (possibleValue) {
      slotToSet = possibleValue;
    }

    form.setValue('slot', slotToSet, { shouldValidate: true });
  }, [slotOptions]);

  useEffect(() => {
    if (
      !isPastEvent &&
      !daySlotsState.loading &&
      daySlotsState.data &&
      employeeValue &&
      !slotValue &&
      (!slotEmployees.find(({ id }) => id === employeeValue) || !slotOptions.length)
    ) {
      form.setError('root.employee', {
        type: 'employee-no-slots',
        message: intl.formatMessage({ id: 'calendarEvent.employee.noSlotsAvailableError' }),
      });
      return;
    }

    if (form.formState.errors.root?.employee?.type === 'employee-no-slots') {
      form.clearErrors('root.employee');
    }
  }, [slotEmployees, slotValue, employeeValue, daySlotsState.loading, slotOptions, isPastEvent, form.formState.submitCount]);

  useEffect(() => {
    form.trigger('employee');
  }, [servicesValue]);

  const prevSlotsPayload = useRef<Nullable<IGetSlotsByDatesPayload>>(null);
  useEffect(() => {
    if (!shouldLoadSchedulingData) return;

    const payload = {
      serviceIds: allServices
        .filter((service: Service) => serviceIds.includes(service.id))
        .map((service) => service.id)
        .sort(),
      dates: getDateRange(store.timezone, store.appSettings.slotsLeadingDays),
      timestamp: Date.now(),
      timezone: store.timezone,
    };

    let shouldFetch = !prevSlotsPayload.current;

    if (!shouldFetch && prevSlotsPayload.current!.dates[0] !== payload.dates[0]) {
      shouldFetch = true;
    }

    if (!shouldFetch && payload.serviceIds.length !== prevSlotsPayload.current!.serviceIds.length) {
      shouldFetch = true;
    }

    if (!shouldFetch) {
      for (let index = 0; index < payload.serviceIds.length; index += 1) {
        const payloadServiceId = payload.serviceIds[index];
        const prevPayloadServiceId = prevSlotsPayload.current!.serviceIds[index];

        if (payloadServiceId !== prevPayloadServiceId) {
          shouldFetch = true;
          break;
        }
      }
    }

    if (shouldFetch) {
      dispatch(getSlots(payload));

      prevSlotsPayload.current = payload;
    }
  }, [serviceIds, allServices, store, shouldLoadSchedulingData]);

  useEffect(() => {
    if (!shouldLoadSchedulingData) return;

    dispatch(
      getSlotsByDate({
        serviceIds: allServices
          .filter((service: Service) => serviceIds.includes(service.id))
          .map((service) => service.id)
          .sort(),
        date: dateValue,
        timestamp: Date.now(),
        timezone: store.timezone,
      }),
    );
  }, [dateValue, serviceIds, allServices, shouldLoadSchedulingData]);

  useEffect(() => {
    if (!shouldLoadSchedulingData) return;

    if (dateValue) {
      const from = TimeHelper.toDayjs(dateValue, store.timezone);
      dispatch(fetchEmployees({ from, timezone: store.timezone }));
    }
  }, [dateValue, store.timezone, shouldLoadSchedulingData]);

  useEffect(() => {
    dispatch(resetSwapSlots());
  }, [dateValue, servicesValue, employeeValue]);

  useEffect(
    () => () => {
      dispatch(resetDaySlots());
      dispatch(resetSlots());
      dispatch(resetSwapSlots());
    },
    [],
  );

  return (
    <FormProvider {...form}>
      <form>
        <Stack direction="column" spacing={1.5}>
          <Box>
            <AppointmentFormCustomerSection
              size="small"
              isDisabled={customerSectionDisabled}
              schema={appointmentFormSchema}
              noteLoading={isSeriesMember && seriesState.loading}
              noteError={isSeriesMember && !seriesState.loading && seriesState.error !== null
                ? { message: intl.formatMessage({ id: 'common.defaultError' }), onRetry: fetchSeries }
                : null}
            />
          </Box>

          {isSeriesMember && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
              <Button data-testid="abort-customer-button" variant="outlined" onClick={onClose}>
                {intl.formatMessage({ id: 'common.abort' })}
              </Button>
              <AsyncButton data-testid="save-customer-button" variant="contained" onClick={handleSubmitSeriesCustomer}>
                {intl.formatMessage({ id: 'common.save' })}
              </AsyncButton>
            </Stack>
          )}

          {isSeriesMember && (() => {
            const seriesInlineEdit = editingEventId !== null ? (
              <>
                {/*
                  Form values for date/slot/employee/services are seeded by
                  handleToggleEditEvent via form.resetField when a card enters
                  edit mode. Don't add Controller defaultValue or display
                  fallbacks (e.g. value={field.value || X}) here — they cause
                  the displayed value to diverge from what gets saved
                  (display would show X but submit would still send null).
                */}
                {Boolean(servicesValue.length) && (
                  <Controller
                    control={form.control}
                    name="slot"
                    render={({ field }) => (
                      <SlotSelectSection
                        value={field.value}
                        onChange={(nextValue) => {
                          if (nextValue) {
                            field.onChange(nextValue);
                            form.trigger('slot');
                          }
                        }}
                        options={slotOptions}
                        loading={daySlotsState.loading}
                        isWarning={!!form.formState.errors.slot}
                        isDisabled={!!isPastEvent}
                        before={(
                          <Controller
                            name="date"
                            render={({ field: dateField }) => (
                              <FormDatePicker
                                isDisabled={!!isPastEvent}
                                date={dateField.value}
                                suggestedDays={availableDays}
                                onChange={dateField.onChange}
                                timezone={store.timezone}
                              />
                            )}
                          />
                        )}
                        employeeId={employeeValue}
                        swapEnabled={store.settings.swap}
                        swapOptions={swapSlotOptions}
                        onFetchSwapOptionsClick={fetchSwapOptions}
                        swapOptionsLoading={swapSlotsState.loading}
                        swapOptionsError={!!swapSlotsState.error}
                      />
                    )}
                  />
                )}
                <DialogSection title={intl.formatMessage({ id: 'appointment.specificEmployee' })}>
                  <Controller
                    control={form.control}
                    name="employee"
                    render={({ field, fieldState, formState }) => {
                      const fieldError = fieldState.error || formState.errors.root?.employee;
                      return (
                        <EmployeeSelectField
                          values={employeeOptions}
                          disabled={!!isPastEvent}
                          loading={employeesLoading}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          error={!!fieldError}
                          helperText={!['nullable', 'required'].includes(fieldError?.type as string) ? fieldError?.message : undefined}
                          required={isRequired(appointmentFormSchema, field.name)}
                        />
                      );
                    }}
                  />
                </DialogSection>
                {!isPastEvent && (
                  <AsyncButton data-testid="save-button" variant="contained" onClick={onSubmitInlineEdit} fullWidth>
                    {intl.formatMessage({ id: 'common.save' })}
                  </AsyncButton>
                )}
              </>
            ) : null;

            return (
              <SeriesMemberEditBody
                appointment={appointment}
                allServices={allServices}
                isPastEvent={isPastEvent}
                onRetrySeries={fetchSeries}
                onToggleEdit={handleToggleEditEvent}
                onRecordOutcome={(eventId) => setOutcomeEventId(eventId)}
                onUndo={handleUndoEvent}
                undoingEventId={undoingEventId}
                onBookFollowUp={onBookFollowUp || onCopy}
                onCopyToNew={onCopyFromSeries}
                isCopyDisabled={!isSeriesCopyEnabled}
                editingEventId={editingEventId}
                inlineEditContent={seriesInlineEdit}
              />
            );
          })()}

          {!isSeriesMember && Boolean(servicesValue.length) && (
            <Controller
              control={form.control}
              name="slot"
              render={({ field }) => (
                <SlotSelectSection
                  value={field.value}
                  onChange={(nextValue) => {
                    if (nextValue) {
                      field.onChange(nextValue);
                      form.trigger('slot');
                    }
                  }}
                  options={slotOptions}
                  loading={daySlotsState.loading}
                  isWarning={!!form.formState.errors.slot}
                  isDisabled={!!isPastEvent}
                  before={(
                    <Controller
                      name="date"
                      render={({ field: dateField }) => (
                        <FormDatePicker
                          isDisabled={!!isPastEvent}
                          date={dateField.value}
                          suggestedDays={availableDays}
                          onChange={dateField.onChange}
                          timezone={store.timezone}
                        />
                      )}
                    />
                  )}
                  employeeId={employeeValue}
                  swapEnabled={store.settings.swap}
                  swapOptions={swapSlotOptions}
                  onFetchSwapOptionsClick={fetchSwapOptions}
                  swapOptionsLoading={swapSlotsState.loading}
                  swapOptionsError={!!swapSlotsState.error}
                />
              )}
            />
          )}

          {!isSeriesMember && (
            <AppointmentFormServiceSection services={allServices} isDisabled={!!isPastEvent} />
          )}

          {!isSeriesMember && (
            <DialogSection title={intl.formatMessage({ id: 'appointment.specificEmployee' })}>
              <Controller
                control={form.control}
                name="employee"
                render={({ field, fieldState, formState }) => {
                  const fieldError = fieldState.error || formState.errors.root?.employee;
                  return (
                    <EmployeeSelectField
                      values={employeeOptions}
                      disabled={!!isPastEvent}
                      loading={employeesLoading}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!fieldError}
                      helperText={!['nullable', 'required'].includes(fieldError?.type as string) ? fieldError?.message : undefined}
                      required={isRequired(appointmentFormSchema, field.name)}
                    />
                  );
                }}
              />
            </DialogSection>
          )}

          {!isSeriesMember && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="column" spacing={1.5} flex={1}>
              {!isPastEvent && (
                <Stack direction="row">
                  <Button data-testid="delete-button" variant="outlined" onClick={onDelete}>
                    {intl.formatMessage({ id: 'common.delete' })}
                  </Button>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} flex={1}>
                    <Button data-testid="abort-button" variant="outlined" onClick={onClose}>
                      {intl.formatMessage({ id: 'common.abort' })}
                    </Button>
                    <AsyncButton data-testid="save-button" variant="contained" onClick={onSubmit}>
                      {intl.formatMessage({ id: 'common.save' })}
                    </AsyncButton>
                  </Stack>
                </Stack>
              )}

              {!isSeriesMember && (
                <Button
                  className={styles.copyToNewButton}
                  data-testid="copy-to-new-button"
                  variant="outlined"
                  onClick={onCopy}
                  disabled={!isCopyEnabled}
                >
                  {intl.formatMessage({ id: 'appointment.copyToNew' })}
                </Button>
              )}

              {form.formState.isSubmitted && !(form.formState.isValid && !Object.keys(form.formState.errors).length) && (
                <FormHelperText error>{intl.formatMessage({ id: 'common.formHasErrors' })}</FormHelperText>
              )}
            </Stack>
          </Stack>
          )}

          <BookingChannelBadge
            source={appointment.source}
            createdAt={appointment.createdAt}
            timezone={store.timezone}
          />
        </Stack>
      </form>

      {isSeriesMember && appointment.seriesId && outcomeEventId !== null && (() => {
        // Resolve which event the modal should target. Prefer the originally-
        // opened appointment when its id matches; otherwise look up in the
        // loaded series events.
        let outcomeEvent: Appointment | null = null;
        if (outcomeEventId === appointment.id) {
          outcomeEvent = appointment;
        } else if (seriesState.data) {
          outcomeEvent = seriesState.data.events.find((e) => e.id === outcomeEventId) || null;
        }
        if (!outcomeEvent) {
          return null;
        }

        let followUpLabel = '';
        if (seriesState.data) {
          const isRoot = outcomeEvent.id === seriesState.data.series.rootEventId;
          if (isRoot) {
            followUpLabel = intl.formatMessage({ id: 'service.firstConsultation' });
          } else {
            const sorted = seriesState.data.events.slice().sort((a, b) => {
              const am = new Date(a.date).getTime() + a.time * 60000;
              const bm = new Date(b.date).getTime() + b.time * 60000;
              return am - bm;
            });
            const idx = sorted.findIndex((e) => e.id === outcomeEvent!.id);
            followUpLabel = intl.formatMessage(
              { id: 'seriesbooking.appointment.followUp' },
              { n: idx },
            );
          }
        }

        const eventTypeData = outcomeEvent.eventTypeData as any;
        let customerName = '';
        if (eventTypeData && eventTypeData.customer) {
          const c = eventTypeData.customer;
          customerName = `${c.name || ''} ${c.lastName || ''}`.trim();
        }

        return (
          <RecordOutcomeModal
            open
            onClose={() => setOutcomeEventId(null)}
            seriesId={appointment.seriesId as string}
            event={outcomeEvent}
            followUpLabel={followUpLabel}
            customerName={customerName}
          />
        );
      })()}
    </FormProvider>
  );
}

function EditBreakForm({ appointment, onCopy, onClose, onDelete, isPastEvent, onSubmit: propsOnSubmit }: EditCalendarEventFormProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const form = useForm<IBreakFormValues>({
    defaultValues: calendarEventToBreakFormValues(appointment),
    resolver: yupResolver(breakFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const dateValue = useWatch({ control: form.control, name: 'date' });
  const employeeValue = useWatch({ control: form.control, name: 'employee' });

  const store = useAppSelector(selectCurrentStore);
  const storeEmployees = useAppSelector((state) => state.employees.storeEmployees.data.employees, shallowEqual);
  const employeesLoading = useAppSelector((state) => state.employees.storeEmployees.loading);
  const availableEmployees = useAppSelector((state) => selectAvailableEmployees(state, [], dateValue), shallowEqual);

  const onSubmit = form.handleSubmit(async (values: IBreakFormValues) => {
    let time = 0;
    let duration: number | undefined;
    if (values.from != null && values.to != null) {
      time = values.from;
      duration = values.to - values.from;
    }

    let title: string | undefined;
    if (values.title != null) {
      title = values.title;
    }

    const params: IUpdateCalendarEventQueryParams = {
      date: values.date,
      time,
      type: CalendarEventTypeEnum.BREAK,
      title,
      note: values.note || '',
      duration,
      eventTypeData: {
        suggestedEmployee: typeof values.employee !== 'undefined' ? values.employee : undefined,
      },
    };

    await propsOnSubmit(appointment.id, params);
  });

  const employeeOptions = useMemo(
    () => (
      createEmployeeOptions({
        employeeId: employeeValue,
        storeEmployees,
        availableEmployees,
      })
    ),
    [availableEmployees, employeeValue, storeEmployees],
  );

  const isCopyEnabled = useMemo(() => {
    if (!appointment) {
      return false;
    }
    const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
    return isPastEvent || !isDirty;
  }, [appointment, form.formState, isPastEvent]);

  useEffect(() => {
    if (dateValue) {
      const from = TimeHelper.toDayjs(dateValue, store.timezone);
      dispatch(fetchEmployees({ from, timezone: store.timezone }));
    }
  }, [dateValue, store]);

  return (
    <FormProvider {...form}>
      <form>
        <Stack direction="column" spacing={1.5}>
          <AppointmentFormInformationSection isDisabled={isPastEvent} />

          <DialogSection title={intl.formatMessage({ id: 'appointment.date' })}>
            <Controller
              name="date"
              render={({ field }) => (
                <FormDatePicker
                  date={field.value}
                  onChange={field.onChange}
                  isDisabled={!!isPastEvent}
                  timezone={store.timezone}
                />
              )}
            />

            <AppointmentFormTimeSection isDisabled={!!isPastEvent} />
          </DialogSection>

          <DialogSection title={intl.formatMessage({ id: 'appointment.specificEmployee' })}>
            <Controller
              control={form.control}
              name="employee"
              render={({ field, fieldState }) => (
                <EmployeeSelectField
                  values={employeeOptions}
                  disabled={!!isPastEvent}
                  loading={employeesLoading}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={!!fieldState.error}
                  helperText={
                    !['nullable', 'required'].includes(fieldState.error?.type as string) ? fieldState.error?.message : undefined
                  }
                  required={isRequired(appointmentFormSchema, field.name)}
                />
              )}
            />
          </DialogSection>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="column" spacing={1.5} flex={1}>
              {!isPastEvent && (
                <Stack direction="row">
                  <Button data-testid="delete-button" variant="outlined" onClick={onDelete}>
                    {intl.formatMessage({ id: 'common.delete' })}
                  </Button>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} flex={1}>
                    <Button data-testid="abort-button" variant="outlined" onClick={onClose}>
                      {intl.formatMessage({ id: 'common.abort' })}
                    </Button>
                    <AsyncButton data-testid="save-button" variant="contained" onClick={onSubmit}>
                      {intl.formatMessage({ id: 'common.save' })}
                    </AsyncButton>
                  </Stack>
                </Stack>
              )}

              <Button
                className={styles.copyToNewButton}
                data-testid="copy-to-new-button"
                variant="outlined"
                onClick={onCopy}
                disabled={!isCopyEnabled}
              >
                {intl.formatMessage({ id: 'appointment.copyToNew' })}
              </Button>

              {form.formState.isSubmitted && !(form.formState.isValid && !Object.keys(form.formState.errors).length) && (
                <FormHelperText error>{intl.formatMessage({ id: 'common.formHasErrors' })}</FormHelperText>
              )}
            </Stack>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
}

function EditBlockerForm({ appointment, onCopy, onClose, onDelete, isPastEvent, onSubmit: propsOnSubmit }: EditCalendarEventFormProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const form = useForm<IBlockerFormValues>({
    defaultValues: calendarEventToBlockerFormValues(appointment),
    resolver: yupResolver(blockerFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const dateValue = useWatch({ control: form.control, name: 'date' });
  const employeeValue = useWatch({ control: form.control, name: 'employee' });

  const store = useAppSelector(selectCurrentStore);
  const storeEmployees = useAppSelector((state) => state.employees.storeEmployees.data.employees, shallowEqual);
  const employeesLoading = useAppSelector((state) => state.employees.storeEmployees.loading);
  const availableEmployees = useAppSelector((state) => selectAvailableEmployees(state, [], dateValue), shallowEqual);

  const onSubmit = form.handleSubmit(async (values) => {
    let time = 0;
    let duration: number | undefined;
    if (values.from != null && values.to != null) {
      time = values.from;
      duration = values.to - values.from;
    }

    let title: string | undefined;
    if (values.title != null) {
      title = values.title;
    }

    const params: IUpdateCalendarEventQueryParams = {
      date: values.date,
      time,
      type: CalendarEventTypeEnum.BLOCKER,
      title,
      note: values.note || '',
      duration,
      eventTypeData: {
        suggestedEmployee: typeof values.employee !== 'undefined' ? values.employee : undefined,
      },
    };

    await propsOnSubmit(appointment.id, params);
  });

  const employeeOptions = useMemo(
    () => (
      createEmployeeOptions({
        employeeId: employeeValue,
        storeEmployees,
        availableEmployees,
      })
    ),
    [availableEmployees, employeeValue, storeEmployees],
  );

  const isCopyEnabled = useMemo(() => {
    if (!appointment) {
      return false;
    }
    const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
    return isPastEvent || !isDirty;
  }, [appointment, form.formState, isPastEvent]);

  useEffect(() => {
    if (dateValue) {
      const from = TimeHelper.toDayjs(dateValue, store.timezone);
      dispatch(fetchEmployees({ from, timezone: store.timezone }));
    }
  }, [dateValue, store]);

  return (
    <FormProvider {...form}>
      <form>
        <Stack direction="column" spacing={1.5}>
          <AppointmentFormInformationSection isDisabled={isPastEvent} />

          <DialogSection title={intl.formatMessage({ id: 'appointment.date' })}>
            <Controller
              name="date"
              render={({ field }) => (
                <FormDatePicker
                  date={field.value}
                  onChange={field.onChange}
                  isDisabled={!!isPastEvent}
                  timezone={store.timezone}
                />
              )}
            />

            <AppointmentFormTimeSection isDisabled={!!isPastEvent} />
          </DialogSection>

          <DialogSection title={intl.formatMessage({ id: 'appointment.specificEmployee' })}>
            <Controller
              control={form.control}
              name="employee"
              render={({ field, fieldState }) => (
                <EmployeeSelectField
                  values={employeeOptions}
                  disabled={!!isPastEvent}
                  loading={employeesLoading}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={!!fieldState.error}
                  helperText={
                    !['nullable', 'required'].includes(fieldState.error?.type as string) ? fieldState.error?.message : undefined
                  }
                  required={isRequired(appointmentFormSchema, field.name)}
                />
              )}
            />
          </DialogSection>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="column" spacing={1.5} flex={1}>
              {!isPastEvent && (
                <Stack direction="row">
                  <Button data-testid="delete-button" variant="outlined" onClick={onDelete}>
                    {intl.formatMessage({ id: 'common.delete' })}
                  </Button>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} flex={1}>
                    <Button data-testid="abort-button" variant="outlined" onClick={onClose}>
                      {intl.formatMessage({ id: 'common.abort' })}
                    </Button>
                    <AsyncButton data-testid="save-button" variant="contained" onClick={onSubmit}>
                      {intl.formatMessage({ id: 'common.save' })}
                    </AsyncButton>
                  </Stack>
                </Stack>
              )}

              <Button
                className={styles.copyToNewButton}
                data-testid="copy-to-new-button"
                variant="outlined"
                onClick={onCopy}
                disabled={!isCopyEnabled}
              >
                {intl.formatMessage({ id: 'appointment.copyToNew' })}
              </Button>

              {form.formState.isSubmitted && !(form.formState.isValid && !Object.keys(form.formState.errors).length) && (
                <FormHelperText error>{intl.formatMessage({ id: 'common.formHasErrors' })}</FormHelperText>
              )}
            </Stack>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
}

export function EditCalendarEvent({ appointmentId, employeeId, onClose, onCopyToNew, shouldReload, isPastEvent, type }: Props) {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const appointmentsState = useAppSelector((state) => state.appointments);
  const store = useAppSelector(selectCurrentStore);
  const calendarSlotsState = useAppSelector((state) => state.slots.slots, shallowEqual);
  const daySlotsState = useAppSelector((state) => state.slots.dateSlots, shallowEqual);
  const allServices = useAppSelector(selectStoreServices, shallowEqual);
  const seriesStateParent = useAppSelector((state) => state.series.getSeriesById, shallowEqual);

  const [calendarEvent, setCalendarEvent] = useState<Appointment | undefined>(undefined);
  const [isConfirmationDialogOpened, setIsConfirmationDialogOpened] = useState<boolean>(false);
  const [slotsErrorShown, setSlotsErrorShown] = useState(false);

  // Ensures we dispatch `getCalendarEvent` at most once per `appointmentId`.
  // Without this guard, a synchronous `resetSingleAppointment()` from the
  // close handler clears `singleAppointment` before the modal unmounts, the
  // mount effect re-runs against the stale `appointmentId`, and the resulting
  // `getOne/fulfilled` re-populates `singleAppointment` post-close — leaving
  // subsequent opens stuck on the stale appointment.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [appointmentId]);

  useEffect(() => {
    if (appointmentsState.loading) {
      return;
    }

    if (shouldReload) {
      const single = appointmentsState.singleAppointment;
      if (single !== null && single.id === appointmentId) {
        setCalendarEvent(single);
      } else if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        dispatch(getCalendarEvent({ appointmentId }));
      }
    } else {
      let nextCalendarEvent = appointmentsState.data.find((appointmentItem) => appointmentItem.id === appointmentId);
      if (nextCalendarEvent && calendarEvent?.id !== nextCalendarEvent?.id) {
        const { overrides } = nextCalendarEvent;
        if (overrides) {
          const overridenNextAppointment = {
            ...nextCalendarEvent,
            time: overrides.time,
            duration: overrides.duration,
            eventTypeData: {
              ...nextCalendarEvent.eventTypeData,
              services: overrides.services,
              pinnedEmployee: overrides.pinnedEmployee,
            },
          };
          nextCalendarEvent = overridenNextAppointment;
        }
        setCalendarEvent(nextCalendarEvent);
      }
    }
  }, [appointmentsState, calendarEvent, appointmentId]);

  const onCopy = useCallback(() => {
    if (calendarEvent) {
      const appointmentParams = calendarEventToCalendarCreateValues(calendarEvent, allServices, employeeId, store.timezone);
      onCopyToNew(appointmentParams);
      onClose();
    }
  }, [calendarEvent, allServices, employeeId]);

  const onBookFollowUp = useCallback(() => {
    const seriesData = seriesStateParent.data;
    if (!seriesData) {
      return;
    }

    if (!store || !store.settings.series || !store.settings.series.followUpServiceId) {
      return;
    }

    const followUpId = store.settings.series.followUpServiceId;

    let seriesEmployeeId: string | undefined;

    seriesEmployeeId = seriesData.series.employeeId;
    if (!seriesEmployeeId && seriesData.series.rootEventId) {
      const rootEvent = seriesData.events.find((e) => e.id === seriesData.series.rootEventId);
      if (rootEvent) {
        seriesEmployeeId = (rootEvent.eventTypeData as any)?.suggestedEmployee;
      }
    }

    const eventData = {
      type: CalendarEventTypeEnum.APPOINTMENT,
      eventTypeData: {
        customerId: seriesData.series.customerId,
        customer: (seriesData.events[0].eventTypeData as any).customer,
        services: [followUpId],
        suggestedEmployee: seriesEmployeeId,
      },
      seriesId: seriesData.series.id,
    } as Appointment;

    const appointmentParams = calendarEventToCalendarCreateValues(eventData, allServices, seriesEmployeeId, store.timezone);
    onCopyToNew(appointmentParams);
    onClose();
  }, [allServices, store, seriesStateParent]);

  const firstConsultServiceId = (store?.settings as any)?.series?.firstServiceId as string | undefined;
  const onCopyFromSeries = useMemo(() => {
    if (!firstConsultServiceId) return undefined;

    const seriesData = seriesStateParent.data;
    if (!seriesData) {
      return undefined;
    }

    return () => {
      if (!calendarEvent) return;

      let seriesEmployeeId: string | undefined;
      if (seriesData) {
        seriesEmployeeId = seriesData.series.employeeId;
        if (!seriesEmployeeId && seriesData.series.rootEventId) {
          const rootEvent = seriesData.events.find((e) => e.id === seriesData.series.rootEventId);
          if (rootEvent) {
            seriesEmployeeId = (rootEvent.eventTypeData as any)?.suggestedEmployee;
          }
        }
      }

      const modifiedEvent = {
        type: CalendarEventTypeEnum.APPOINTMENT,
        eventTypeData: {
          customer: (seriesData.events[0].eventTypeData as any).customer,
          services: [firstConsultServiceId],
          suggestedEmployee: seriesEmployeeId,
        },
        note: seriesData.series.note,
      } as Appointment;

      const appointmentParams = calendarEventToCalendarCreateValues(modifiedEvent, allServices, seriesEmployeeId, store.timezone);
      onCopyToNew(appointmentParams);
      onClose();
    };
  }, [firstConsultServiceId, calendarEvent, allServices, store, seriesStateParent, onCopyToNew, onClose]);

  const openDeleteConfirmationDialog = useCallback(() => {
    setIsConfirmationDialogOpened(true);
  }, []);

  const closeDeleteConfirmationDialog = useCallback(() => {
    setIsConfirmationDialogOpened(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!calendarEvent) return;
    dispatch(deleteCalendarEvents({ id: calendarEvent.id, reason: 'delete reason' }) as any);
    onClose();
  }, [calendarEvent]);

  const onSubmit = useCallback(
    async (appointmentId: string, params: IUpdateCalendarEventQueryParams) => {
      if (!store || !calendarEvent) return;

      try {
        dispatch(updateCalendarEvents({ appointmentId, params }));
        onClose();
      } catch (error) {
        /* empty */
      }
    },
    [store, calendarEvent],
  );

  useEffect(() => {
    setSlotsErrorShown(Boolean(calendarSlotsState.error || daySlotsState.error));
  }, [calendarSlotsState.error, daySlotsState.error]);

  useEffect(
    () => () => {
      dispatch(resetUpdateCalendarEvent());
    },
    [],
  );

  if (!calendarEvent || !store) {
    return <></>;
  }

  return (
    <>
      <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} open onClick={onClose}>
        <Paper data-testid="edit-appointment-paper" className={styles.formWrapper} onClick={(event) => event.stopPropagation()}>
          <Stack direction="column" spacing={1.5} className={styles.formContent}>
            <Stack direction="row" justifyContent="space-between">
              <Typography className={styles.formTitle}>
                {intl.formatMessage(
                  { id: 'common.editEntity' },
                  { entity: intl.formatMessage({ id: `appointment.${calendarEvent.type}` }) },
                )}
              </Typography>
              <IconButton data-testid="edit-calendar-event-close-button" size="small" className={styles.actionIconButton} onClick={onClose}>
                <CloseIcon className={styles.actionIcon} />
              </IconButton>
            </Stack>
            {type === CalendarEventTypeEnum.APPOINTMENT && (
              <EditAppointmentForm
                appointment={calendarEvent}
                isPastEvent={isPastEvent}
                onClose={onClose}
                onCopy={onCopy}
                onBookFollowUp={onBookFollowUp}
                onCopyFromSeries={onCopyFromSeries}
                onDelete={openDeleteConfirmationDialog}
                onSubmit={onSubmit}
              />
            )}
            {type === CalendarEventTypeEnum.BREAK && (
              <EditBreakForm
                appointment={calendarEvent}
                isPastEvent={isPastEvent}
                onClose={onClose}
                onCopy={onCopy}
                onDelete={openDeleteConfirmationDialog}
                onSubmit={onSubmit}
              />
            )}
            {type === CalendarEventTypeEnum.BLOCKER && (
              <EditBlockerForm
                appointment={calendarEvent}
                isPastEvent={isPastEvent}
                onClose={onClose}
                onCopy={onCopy}
                onDelete={openDeleteConfirmationDialog}
                onSubmit={onSubmit}
              />
            )}
          </Stack>
        </Paper>
      </Backdrop>

      <Dialog open={isConfirmationDialogOpened}>
        <DialogTitle>{intl.formatMessage({ id: 'calendar.deleteAppointmentDialogTitle' })}</DialogTitle>
        <DialogContent>{intl.formatMessage({ id: 'calendar.deleteAppoinctmentDialog' })}</DialogContent>
        <DialogActions>
          <Button data-testid="delete-dialog-close" autoFocus onClick={closeDeleteConfirmationDialog}>
            {intl.formatMessage({ id: 'common.abort' })}
          </Button>
          <Button data-testid="delete-dialog-confirm" onClick={handleDelete}>
            {intl.formatMessage({ id: 'common.confirm' })}
          </Button>
        </DialogActions>
      </Dialog>

      <NoticeStack>
        <Notice
          open={slotsErrorShown}
          onClose={() => setSlotsErrorShown(false)}
          severity="error"
          autoHideDuration={60000}
          title={intl.formatMessage({ id: 'slot.slotsFetchErrorTitle' })}
        />
      </NoticeStack>
    </>
  );
}

EditCalendarEvent.defaultProps = {
  employeeId: undefined,
  shouldReload: undefined,
  isPastEvent: undefined,
};
